import { type ComponentProps, type FC, useMemo } from "react";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useWeekIndex } from "../../contexts/WeekIndexProvider";
import { useRosterLookup } from "../../hooks/useRosterLookup";
import { useScheduleDayOf } from "../../hooks/useScheduleDayOf";
import { useScheduleCacheEnabled } from "../../hooks/useScheduleWeekRules";
import { useScheduleWeeks } from "../../hooks/useScheduleWeeks";
import type { StaffingIssue } from "../../utils/autoStaffing";
import { getWeekStart } from "../../utils/dateUtils";
import { DEFAULT_WEEK_START_DAY, type OpenShiftRequest, type ReplaceShiftRequest, type ScheduleData } from "../../utils/models";
import { getFirstLoadedDate } from "../../utils/panelWeeks";
import { Recommendation } from "../Recommendation/Recommendation";
import { PanelDayFallback } from "../StaffingChanges/PanelDayFallback";

type Props = Omit<ComponentProps<typeof Recommendation>, "schedule" | "weekIndex" | "startingDate" | "request"> & {
	request: OpenShiftRequest | ReplaceShiftRequest;
};

/**
 * A request's card in the demo Requests tab. With `staffing_schedule_cache` off it reads the old list as before; on, the
 * weeks around the request's own day, with its suggestions placed in them, so a request on any day of the schedule works.
 */
export const RequestRecommendationCard: FC<Props> = ({ rec, request, ...cardProps }) => {
	const isCacheEnabled = useScheduleCacheEnabled();
	const { hospitalData } = useHospitalData();
	const { weekIndex } = useWeekIndex();
	const { chosenDepartment } = useChosenDepartment();
	const scheduleDayOf = useScheduleDayOf();
	const requestDay = scheduleDayOf({ startTime: request.message.shift_start, shiftType: request.message.shift_type });
	const requestWeeks = useScheduleWeeks(requestDay.date);
	const { roster } = useRosterLookup(chosenDepartment.department_id);
	const slot = requestWeeks.slotOf(requestDay.date, requestDay.shiftType);

	const placedRec = useMemo<StaffingIssue>(
		() => ({
			...rec,
			suggestions: rec.suggestions.map((suggestion) => ({
				...suggestion,
				// DEFAULT: -1 for a request on a day without its shift type's row, as before; accepting it reports the day isn't loaded.
				shiftNo: slot?.shiftIndex ?? -1,
				dayNo: slot?.dayIndex ?? -1,
				weekIndex: slot?.weekIndex ?? -1,
			})),
		}),
		[rec, slot?.shiftIndex, slot?.dayIndex, slot?.weekIndex],
	);
	const firstLoadedDate = getFirstLoadedDate(requestWeeks.weeks);
	const startingDate = useMemo(() => getWeekStart(firstLoadedDate ?? requestDay.date, DEFAULT_WEEK_START_DAY), [firstLoadedDate, requestDay.date]);
	const schedule = useMemo<ScheduleData | undefined>(() => roster && { weeks: requestWeeks.weeks, nurses: roster }, [requestWeeks.weeks, roster]);

	if (!isCacheEnabled) {
		return (
			<Recommendation
				{...cardProps}
				rec={rec}
				request={request}
				schedule={hospitalData}
				weekIndex={weekIndex}
				startingDate={hospitalData?.weeks[0]?.weeklyShifts[0]?.days[0]?.date}
			/>
		);
	}
	// The weeks wait for the roster too, so a schedule is missing only while they load or after they fail.
	if (requestWeeks.isLoading || requestWeeks.isError || !schedule)
		return <PanelDayFallback panelDay={requestWeeks} dataTestId={`request-card-${request.id}`} />;
	return <Recommendation {...cardProps} rec={placedRec} request={request} schedule={schedule} weekIndex={slot?.weekIndex ?? -1} startingDate={startingDate} />;
};
